/**
 * Whether a credit note this client booked, and its supplier has not yet reported, reduces the
 * input credit claimed (D-102, W3-18 (4)).
 *
 * The GST team's answer (Q3, 2026-10-13) was that this is asked of each client, not settled for
 * the firm: some clients want the claim lowered as soon as the note is booked, others wait for the
 * supplier. **Until it is asked the answer is "not asked"**, and the 3B says so beside the notes
 * rather than assuming either.
 */
import { Alert, Button } from '@fiveox/ui'
import { useState } from 'react'

import { CLIENT, useEvaluation } from '../lib/access'
import { ApiError, clients } from '../lib/api'

export const CREDIT_NOTE_CHOICES: { value: boolean | null; label: string; says: string }[] = [
  {
    value: null,
    label: 'Not asked yet',
    says: 'The 3B lists such notes and says the question is open for this client.',
  },
  {
    value: true,
    label: 'Reduce the claim',
    says: 'A credit note the client booked reduces the claim in the month it is booked.',
  },
  {
    value: false,
    label: 'List them only',
    says: 'The claim stays as the 2B has it until the supplier reports the note.',
  },
]

export function CreditNoteSettingCard({
  clientId,
  value,
  onSaved,
}: {
  clientId: string
  value: boolean | null
  onSaved: (next: boolean | null) => void
}) {
  const { can } = useEvaluation()
  const editable = can(CLIENT, 'can_update')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const current = CREDIT_NOTE_CHOICES.find((c) => c.value === value) ?? CREDIT_NOTE_CHOICES[0]!

  const choose = async (next: boolean | null) => {
    setBusy(true)
    setError(null)
    try {
      await clients.update(clientId, { reduce_booked_credit_notes: next })
      onSaved(next)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That setting could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="gst-card p-4">
      <p className="gst-panel-h mb-2">Credit notes booked before the 2B shows them</p>
      <p className="gst-meta mb-3">
        A credit note the client has booked and the supplier has not yet reported: does it reduce
        this client&rsquo;s input credit now? Changing this sends back any GSTR-3B that is signed
        off or with the client.
      </p>
      <p className="mb-1 font-medium">{current.label}</p>
      <p className="gst-meta mb-3">{current.says}</p>
      {editable && (
        <div className="flex flex-wrap gap-2">
          {CREDIT_NOTE_CHOICES.filter((c) => c.value !== value).map((c) => (
            <Button
              key={String(c.value)}
              type="button"
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => void choose(c.value)}
            >
              {c.label}
            </Button>
          ))}
        </div>
      )}
      {error && <Alert tone="error" className="mt-3">{error}</Alert>}
    </section>
  )
}
